// WebRTC Data Channel File Sharing Service
// Handles peer-to-peer file transfer via data channels

class FileShareService {
  constructor() {
    this.dataChannels = {}; // { peerId: dataChannel }
    this.fileTransfers = {}; // { transferId: { chunks, received, total, filename } }
    this.chunkSize = 64 * 1024; // 64KB chunks
  }

  // Create data channel for file sharing
  createDataChannel(peerConnection, peerId) {
    try {
      const dataChannel = peerConnection.createDataChannel('file-share', {
        ordered: true
      });
      this.setupDataChannel(dataChannel, peerId);
      console.log('📤 Created data channel for', peerId);
      return dataChannel;
    } catch (error) {
      console.error('❌ Error creating data channel:', error);
      return null;
    }
  }

  // Handle incoming data channel
  handleDataChannel(event, peerId) {
    const dataChannel = event.channel;
    this.setupDataChannel(dataChannel, peerId);
    console.log('📥 Received data channel from', peerId);
  }

  // Setup data channel handlers
  setupDataChannel(dataChannel, peerId) {
    this.dataChannels[peerId] = dataChannel;

    dataChannel.onopen = () => {
      console.log('📡 Data channel opened for', peerId);
    };

    dataChannel.onclose = () => {
      console.log('📡 Data channel closed for', peerId);
      delete this.dataChannels[peerId];
    };

    dataChannel.onerror = (error) => {
      console.error('❌ Data channel error:', error);
    };

    dataChannel.onmessage = (event) => {
      this.handleMessage(event.data, peerId);
    };
  }

  // Send file to peer
  sendFile(peerId, file) {
    const dataChannel = this.dataChannels[peerId];
    if (!dataChannel || dataChannel.readyState !== 'open') {
      console.error('❌ Data channel not ready for', peerId);
      return false;
    }

    const reader = new FileReader();
    const transferId = `${Date.now()}_${Math.random()}`;
    let offset = 0;

    reader.onload = (e) => {
      const fileData = e.target.result;
      const chunks = [];
      
      // Split file into chunks
      for (let i = 0; i < fileData.byteLength; i += this.chunkSize) {
        chunks.push(fileData.slice(i, i + this.chunkSize));
      }

      console.log(`📁 Sending file "${file.name}" (${chunks.length} chunks) to ${peerId}`);

      // Send file metadata
      dataChannel.send(JSON.stringify({
        type: 'file-start',
        transferId,
        fileName: file.name,
        fileSize: file.size,
        totalChunks: chunks.length
      }));

      // Send chunks
      chunks.forEach((chunk, index) => {
        setTimeout(() => {
          dataChannel.send(chunk);
          console.log(`📤 Sent chunk ${index + 1}/${chunks.length}`);
        }, index * 10); // Small delay between chunks to avoid buffer overflow
      });

      // Send completion message
      setTimeout(() => {
        dataChannel.send(JSON.stringify({
          type: 'file-end',
          transferId
        }));
        console.log(`✅ File transfer complete for "${file.name}"`);
      }, chunks.length * 10);
    };

    reader.readAsArrayBuffer(file);
    return true;
  }

  // Handle incoming messages
  handleMessage(data, peerId) {
    if (typeof data === 'string') {
      try {
        const message = JSON.parse(data);
        if (message.type === 'file-start') {
          this.handleFileStart(message, peerId);
        } else if (message.type === 'file-end') {
          this.handleFileEnd(message, peerId);
        }
      } catch (e) {
        console.error('❌ Error parsing message:', e);
      }
    } else if (data instanceof ArrayBuffer) {
      this.handleFileChunk(data, peerId);
    }
  }

  // Handle file start message
  handleFileStart(message, peerId) {
    const { transferId, fileName, fileSize, totalChunks } = message;
    this.fileTransfers[transferId] = {
      chunks: [],
      received: 0,
      total: totalChunks,
      fileName,
      fileSize,
      peerId
    };
    console.log(`📥 Starting file transfer: "${fileName}" (${totalChunks} chunks)`);
  }

  // Handle file chunk
  handleFileChunk(chunk, peerId) {
    const transferIds = Object.keys(this.fileTransfers).filter(
      id => this.fileTransfers[id].peerId === peerId
    );

    if (transferIds.length === 0) return;

    const transferId = transferIds[0];
    const transfer = this.fileTransfers[transferId];
    transfer.chunks.push(new Uint8Array(chunk));
    transfer.received++;

    console.log(`📥 Received chunk ${transfer.received}/${transfer.total}`);
  }

  // Handle file end message
  handleFileEnd(message, peerId) {
    const { transferId } = message;
    const transfer = this.fileTransfers[transferId];

    if (!transfer) {
      console.error('❌ Transfer not found:', transferId);
      return;
    }

    // Reconstruct file from chunks
    const blob = new Blob(transfer.chunks, { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    
    // Trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = transfer.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`✅ File received and downloaded: "${transfer.fileName}"`);
    delete this.fileTransfers[transferId];
  }
}

export default new FileShareService();
